import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WOFinancialSummary } from "./WOFinancialSummary";
import { WOBillingGrid } from "./WOBillingGrid";
import { WODrawGrid } from "./WODrawGrid";
import { WODrawHistory } from "./WODrawHistory";
import { WOCompletionGrid } from "./WOCompletionGrid";
import { WORetainageTab } from "./WORetainageTab";
import { WOAdjustmentsTab } from "./WOAdjustmentsTab";
import { WOCreateBillDialog } from "./WOCreateBillDialog";
import { WOBillsList } from "./WOBillsList";
import { useWOAdjustments, computeAdjustmentTotals } from "@/hooks/useWOAdjustments";
import { useWODraws } from "@/hooks/useWODraws";
import { useQuery } from "@tanstack/react-query";
import { useUnitAssignmentDetails } from "@/hooks/useUnitSchedule";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ChevronRight, ChevronDown, Trash2, Receipt, Pencil, Check, X } from "lucide-react";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtRate = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);
const fmtQty = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 2 });
const pct = (n: number) => `${n.toFixed(1)}%`;

interface Props {
  wo: any;
  linesForWo: any[];
  billed: number;
  projectId: string;
  project?: any;
  sovLines?: any[];
  onMutate: () => void;
  billingType?: string;
}

export const WOExpandedContent = ({ wo, linesForWo, billed, projectId, project, sovLines, onMutate, billingType }: Props) => {
  const { data: adjustments } = useWOAdjustments(wo.id);
  const { data: unitAssignmentDetails } = useUnitAssignmentDetails(projectId);
  const { data: draws } = useWODraws(wo.id);
  const { can, isAdmin } = usePermissions();
  const adjTotals = computeAdjustmentTotals(adjustments ?? []);
  const woTotal = linesForWo.reduce((s: number, wol: any) => s + Number(wol.quantity) * Number(wol.contract_rate), 0);
  const isSovOnly = billingType === "sov_only" || billingType === "mixed";
  const isUnitBased = billingType === "unit_based";
  const canEdit = isAdmin || can("edit_work_orders");
  const isBlanket = wo.wo_type === "blanket" || !wo.wo_type;

  // Count existing bills for this WO (for Bills tab badge).
  // Exclude orphaned bills that have no line items (can result from failed creation).
  const { data: billsCount } = useQuery({
    queryKey: ["wo_bills_count", wo.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bills")
        .select("id, bill_lines(id)")
        .eq("work_order_id", wo.id);
      if (error) throw error;
      // Only count bills that have at least one line item
      return (data ?? []).filter((b: any) => b.bill_lines && b.bill_lines.length > 0).length;
    },
  });

  const [editingLine, setEditingLine] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Field editing state (description, qty, rate, unit)
  const [fieldEditingLine, setFieldEditingLine] = useState<string | null>(null);
  const [fieldEditValues, setFieldEditValues] = useState<{
    description: string;
    quantity: string;
    contract_rate: string;
    unit: string;
  }>({ description: "", quantity: "", contract_rate: "", unit: "" });
  const [expandedWoLines, setExpandedWoLines] = useState<Set<string>>(new Set());
  const [deletingLine, setDeletingLine] = useState<string | null>(null);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [showBilled, setShowBilled] = useState(false);

  // Filter out billed lines unless toggle is on
  const visibleLines = useMemo(() => {
    if (showBilled) return linesForWo;
    return linesForWo.filter((wol: any) => wol.status !== "billed");
  }, [linesForWo, showBilled]);

  const billedLineCount = linesForWo.filter((wol: any) => wol.status === "billed").length;

  // Count billable lines (from visible lines)
  const billableCount = visibleLines.filter((wol: any) =>
    ["pending", "complete"].includes(wol.status)
  ).length;

  const getTrackingType = (wol: any): string => {
    if (wol.sov_lines?.tracking_type) return wol.sov_lines.tracking_type;
    if (billingType === "sov_only") return "quantity";
    return "unit";
  };

  // Unit assignments for this WO grouped by sov_line_id
  const unitsByWoLine = useMemo(() => {
    const map: Record<string, any[]> = {};
    (unitAssignmentDetails ?? []).forEach((ua: any) => {
      if (ua.work_order_id === wo.id) {
        if (!map[ua.sov_line_id]) map[ua.sov_line_id] = [];
        map[ua.sov_line_id].push(ua);
      }
    });
    return map;
  }, [unitAssignmentDetails, wo.id]);

  const hasAnyUnits = Object.keys(unitsByWoLine).length > 0;

  // Consolidate lines by sov_line_id (uses visibleLines)
  const consolidatedLines = useMemo(() => {
    const groups: Record<string, any[]> = {};
    visibleLines.forEach((wol: any) => {
      const key = wol.sov_line_id ?? wol.id; // ungrouped lines use own id
      if (!groups[key]) groups[key] = [];
      groups[key].push(wol);
    });

    return Object.entries(groups).map(([key, children]) => {
      if (children.length === 1) {
        // Single line — no consolidation needed, but add unit numbers if available
        const wol = children[0];
        const lineUnits = unitsByWoLine[wol.sov_line_id] ?? [];
        const unitNums = lineUnits.map((u: any) => u.unit_number).filter(Boolean).sort();
        const unitSuffix = unitNums.length > 0
          ? ` · Unit${unitNums.length > 1 ? "s" : ""} ${unitNums.length > 6 ? unitNums.slice(0, 5).join(", ") + ` +${unitNums.length - 5} more` : unitNums.join(", ")}`
          : "";
        return {
          key,
          description: wol.description + unitSuffix,
          qty: Number(wol.quantity),
          rate: Number(wol.contract_rate),
          lineTotal: Number(wol.quantity) * Number(wol.contract_rate),
          completionPct: Number(wol.completion_pct ?? 0),
          earned: Number(wol.earned_amount ?? 0),
          billed: Number(wol.billed_amount ?? 0),
          unit: wol.unit,
          trackingType: getTrackingType(wol),
          childLines: children,
          isSingle: true,
          sovLineId: wol.sov_line_id,
        };
      }

      // Multiple lines for same SOV line — consolidate
      const first = children[0];
      const lineUnits = unitsByWoLine[first.sov_line_id] ?? [];
      const unitNums = children
        .map((c: any) => c.unit_schedule?.unit_number)
        .filter(Boolean)
        .sort();
      // Fallback to unit assignment details if line doesn't have unit_schedule
      const displayUnits = unitNums.length > 0 ? unitNums : lineUnits.map((u: any) => u.unit_number).filter(Boolean).sort();
      const unitSuffix = displayUnits.length > 0
        ? ` · Unit${displayUnits.length > 1 ? "s" : ""} ${displayUnits.length > 6 ? displayUnits.slice(0, 5).join(", ") + ` +${displayUnits.length - 5} more` : displayUnits.join(", ")}`
        : ` · ${children.length} lines`;

      const totalQty = children.reduce((s: number, c: any) => s + Number(c.quantity), 0);
      const rate = Number(first.contract_rate);
      const totalLineVal = children.reduce((s: number, c: any) => s + Number(c.quantity) * Number(c.contract_rate), 0);
      const totalEarned = children.reduce((s: number, c: any) => s + Number(c.earned_amount ?? 0), 0);
      const totalBilled = children.reduce((s: number, c: any) => s + Number(c.billed_amount ?? 0), 0);
      const avgCompletion = children.reduce((s: number, c: any) => s + Number(c.completion_pct ?? 0), 0) / children.length;

      return {
        key,
        description: first.description + unitSuffix,
        qty: totalQty,
        rate,
        lineTotal: totalLineVal,
        completionPct: avgCompletion,
        earned: totalEarned,
        billed: totalBilled,
        unit: first.unit,
        trackingType: getTrackingType(first),
        childLines: children,
        isSingle: false,
        sovLineId: first.sov_line_id,
      };
    });
  }, [visibleLines, unitsByWoLine]);

  const toggleWoLineExpand = (lineId: string) => {
    setExpandedWoLines(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const handleSaveCompletion = async (wol: any, trackingType: string) => {
    setSaving(true);
    try {
      const qty = Number(wol.quantity);
      const rate = Number(wol.contract_rate);
      let completionPct: number;
      let completedQty: number;

      if (trackingType === "lump_sum") {
        completionPct = editValue === "complete" ? 100 : 0;
        completedQty = completionPct === 100 ? qty : 0;
      } else if (trackingType === "quantity") {
        completedQty = Math.min(Number(editValue) || 0, qty);
        completionPct = qty > 0 ? (completedQty / qty) * 100 : 0;
      } else {
        completionPct = Math.min(Number(editValue) || 0, 100);
        completedQty = qty * (completionPct / 100);
      }

      const earned = completedQty * rate;

      const { error } = await supabase
        .from("work_order_lines")
        .update({
          completion_pct: completionPct,
          completed_qty: completedQty,
          earned_amount: earned,
        } as any)
        .eq("id", wol.id);

      if (error) throw error;
      toast.success("Completion updated");
      setEditingLine(null);
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFieldEdit = async (wolId: string) => {
    setSaving(true);
    try {
      const qty = Number(fieldEditValues.quantity);
      const rate = Number(fieldEditValues.contract_rate);
      const { error } = await supabase
        .from("work_order_lines")
        .update({
          description: fieldEditValues.description,
          quantity: qty,
          contract_rate: rate,
          unit: fieldEditValues.unit,
        })
        .eq("id", wolId);

      if (error) throw error;

      // Update WO total amount
      const newTotal = linesForWo.reduce((s: number, wol: any) => {
        if (wol.id === wolId) return s + qty * rate;
        return s + Number(wol.quantity) * Number(wol.contract_rate);
      }, 0);
      await supabase.from("work_orders").update({ amount: newTotal }).eq("id", wo.id);

      toast.success("Line item updated");
      setFieldEditingLine(null);
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLine = async (wol: any) => {
    const billedAmt = Number(wol.billed_amount ?? 0);
    if (billedAmt > 0) return toast.error("Cannot delete — line has billed amounts");
    if (!confirm(`Delete line "${wol.description}"? This cannot be undone.`)) return;

    setDeletingLine(wol.id);
    try {
      const { error } = await supabase.from("work_order_lines").delete().eq("id", wol.id);
      if (error) throw error;

      // Update WO amount
      const remaining = linesForWo.filter(l => l.id !== wol.id);
      const newTotal = remaining.reduce((s: number, l: any) => s + Number(l.quantity) * Number(l.contract_rate), 0);
      await supabase.from("work_orders").update({ amount: newTotal } as any).eq("id", wo.id);

      // Audit log
      await supabase.from("ops_audit_log").insert({
        table_name: "work_order_lines",
        record_id: wol.id,
        action: "wo_line_deleted",
        old_status: wol.status,
        new_status: "deleted",
        changed_by: (await supabase.auth.getUser()).data.user?.id,
        metadata: {
          wo_number: wo.number,
          description: wol.description,
          quantity: wol.quantity,
          rate: wol.contract_rate,
          line_total: Number(wol.quantity) * Number(wol.contract_rate),
          unit_number: wol.unit_schedule?.unit_number ?? null,
        },
      } as any);

      toast.success(`Line "${wol.description}" deleted`);
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeletingLine(null);
    }
  };

  const trackingBadge = (type: string) => {
    const colors: Record<string, string> = {
      unit: "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300",
      quantity: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
      lump_sum: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
    };
    return <Badge variant="outline" className={cn("text-[10px]", colors[type])}>{type.replace("_", " ")}</Badge>;
  };

  return (
    <div className="space-y-4">
      <WOFinancialSummary
        contractAmount={woTotal}
        billedToDate={billed}
        retainagePct={Number(wo.retainage_pct ?? 10)}
        retainageReleased={Number(wo.retainage_released ?? 0)}
        adjustments={adjTotals}
      />

      <Tabs defaultValue="lines">
        <TabsList>
          <TabsTrigger value="lines">Line Items</TabsTrigger>
          {hasAnyUnits && isBlanket && <TabsTrigger value="completion">Completion Grid</TabsTrigger>}
          {hasAnyUnits && isBlanket && (
            <TabsTrigger value="draws">
              Draws
              {(draws ?? []).length > 0 && (
                <span className="ml-1 text-[10px] bg-muted-foreground/20 rounded-full px-1.5">
                  {(draws ?? []).length}
                </span>
              )}
            </TabsTrigger>
          )}
          {hasAnyUnits && isBlanket && <TabsTrigger value="new-draw">New Draw</TabsTrigger>}
          <TabsTrigger value="adjustments">
            Adjustments
            {(adjustments ?? []).length > 0 && (
              <span className="ml-1 text-[10px] bg-muted-foreground/20 rounded-full px-1.5">
                {(adjustments ?? []).length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="retainage">Retainage</TabsTrigger>
          {hasAnyUnits && !isBlanket && <TabsTrigger value="billing">Billing Grid</TabsTrigger>}
          {(billsCount ?? 0) > 0 && (
            <TabsTrigger value="bills">
              Bills
              <span className="ml-1 text-[10px] bg-muted-foreground/20 rounded-full px-1.5">{billsCount}</span>
            </TabsTrigger>
          )}
          {canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && billableCount > 0 && (
            <TabsTrigger value="create-bill" className="text-primary">
              <Receipt className="h-3.5 w-3.5 mr-1" />
              Create Bill
              <span className="ml-1 text-[10px] bg-primary/20 rounded-full px-1.5">{billableCount}</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="lines">
          {billedLineCount > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <Switch checked={showBilled} onCheckedChange={setShowBilled} id="show-billed" />
              <label htmlFor="show-billed" className="text-sm text-muted-foreground cursor-pointer">
                Show billed lines ({billedLineCount})
              </label>
            </div>
          )}
          {consolidatedLines.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>SOV Line</TableHead>
                  {isSovOnly && <TableHead>Type</TableHead>}
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Line Total</TableHead>
                  <TableHead className="text-right">Comp%</TableHead>
                  <TableHead className="text-right">Earned</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consolidatedLines.map((group) => {
                  const isGroupExpanded = expandedWoLines.has(group.key);
                  const hasChildren = group.childLines.length > 1;
                  const singleWol = !hasChildren ? group.childLines[0] : null;
                  const isSingleFieldEditing = singleWol && fieldEditingLine === singleWol.id;

                  return (
                    <>
                      <TableRow
                        key={group.key}
                        className={hasChildren ? "cursor-pointer" : ""}
                        onClick={() => hasChildren && toggleWoLineExpand(group.key)}
                      >
                        <TableCell className="p-2">
                          {hasChildren && (
                            isGroupExpanded
                              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {isSingleFieldEditing ? (
                            <Input
                              value={fieldEditValues.description}
                              onChange={(e) => setFieldEditValues((p) => ({ ...p, description: e.target.value }))}
                              className="h-7 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              {group.description}
                              {hasChildren && <span className="text-[10px] text-muted-foreground ml-1">({group.childLines.length} lines)</span>}
                            </>
                          )}
                        </TableCell>
                        {isSovOnly && <TableCell>{trackingBadge(group.trackingType)}</TableCell>}
                        <TableCell className="text-right font-mono text-sm">
                          {isSingleFieldEditing ? (
                            <Input
                              type="number"
                              value={fieldEditValues.quantity}
                              onChange={(e) => setFieldEditValues((p) => ({ ...p, quantity: e.target.value }))}
                              className="h-7 text-sm w-24 text-right"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            group.qty.toLocaleString()
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {isSingleFieldEditing ? (
                            <Input
                              value={fieldEditValues.unit}
                              onChange={(e) => setFieldEditValues((p) => ({ ...p, unit: e.target.value }))}
                              className="h-7 text-sm w-16"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            group.unit
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {isSingleFieldEditing ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={fieldEditValues.contract_rate}
                              onChange={(e) => setFieldEditValues((p) => ({ ...p, contract_rate: e.target.value }))}
                              className="h-7 text-sm w-24 text-right"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            fmtRate(group.rate)
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium text-sm">
                          {isSingleFieldEditing
                            ? fmt(Number(fieldEditValues.quantity) * Number(fieldEditValues.contract_rate))
                            : fmt(group.lineTotal)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <div className="flex items-center gap-2 justify-end">
                            <Progress value={group.completionPct} className="w-12 h-1.5" />
                            <span>{pct(group.completionPct)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">{fmt(group.earned)}</TableCell>
                        <TableCell className={cn("text-right text-sm", group.billed < group.earned && "text-amber-600")}>
                          {fmt(group.billed)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {isSingleFieldEditing ? (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setFieldEditingLine(null)}>
                                <X className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={saving} onClick={() => handleSaveFieldEdit(singleWol.id)}>
                                <Check className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            !hasChildren && singleWol && canEdit && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => {
                                        setFieldEditingLine(singleWol.id);
                                        setFieldEditValues({
                                          description: singleWol.description ?? "",
                                          quantity: String(Number(singleWol.quantity)),
                                          contract_rate: String(Number(singleWol.contract_rate)),
                                          unit: singleWol.unit ?? "",
                                        });
                                      }}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit line item</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded child lines */}
                      {isGroupExpanded && hasChildren && group.childLines.map((wol: any) => {
                        const qty = Number(wol.quantity);
                        const rate = Number(wol.contract_rate);
                        const lineTotal = qty * rate;
                        const completionPct = Number(wol.completion_pct ?? 0);
                        const earned = Number(wol.earned_amount ?? 0);
                        const billedAmt = Number(wol.billed_amount ?? 0);
                        const isEditing = editingLine === wol.id;
                        const isFieldEditing = fieldEditingLine === wol.id;
                        const trackingType = getTrackingType(wol);
                        const unitLabel = wol.unit_schedule?.unit_number ?? "";

                        return (
                          <TableRow key={wol.id} className="bg-muted/10 border-0 text-sm">
                            <TableCell />
                            <TableCell className="font-mono text-xs pl-6">
                              {isFieldEditing ? (
                                <Input
                                  value={fieldEditValues.description}
                                  onChange={(e) => setFieldEditValues((p) => ({ ...p, description: e.target.value }))}
                                  className="h-7 text-xs"
                                  autoFocus
                                />
                              ) : (
                                unitLabel || wol.description
                              )}
                            </TableCell>
                            {isSovOnly && <TableCell />}
                            <TableCell className="text-right font-mono text-xs">
                              {isFieldEditing ? (
                                <Input
                                  type="number"
                                  value={fieldEditValues.quantity}
                                  onChange={(e) => setFieldEditValues((p) => ({ ...p, quantity: e.target.value }))}
                                  className="h-7 text-xs w-20 text-right"
                                />
                              ) : (
                                qty.toLocaleString()
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {isFieldEditing ? (
                                <Input
                                  value={fieldEditValues.unit}
                                  onChange={(e) => setFieldEditValues((p) => ({ ...p, unit: e.target.value }))}
                                  className="h-7 text-xs w-14"
                                />
                              ) : (
                                wol.unit
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {isFieldEditing ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={fieldEditValues.contract_rate}
                                  onChange={(e) => setFieldEditValues((p) => ({ ...p, contract_rate: e.target.value }))}
                                  className="h-7 text-xs w-24 text-right"
                                />
                              ) : (
                                fmtRate(rate)
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {isFieldEditing
                                ? fmt(Number(fieldEditValues.quantity) * Number(fieldEditValues.contract_rate))
                                : fmt(lineTotal)}
                            </TableCell>
                            <TableCell className="text-right text-xs" onClick={e => e.stopPropagation()}>
                              {isEditing ? (
                                trackingType === "lump_sum" ? (
                                  <div className="flex items-center gap-2 justify-end">
                                    <span className="text-xs text-muted-foreground">Complete</span>
                                    <Switch
                                      checked={editValue === "complete"}
                                      onCheckedChange={(v) => setEditValue(v ? "complete" : "not_started")}
                                    />
                                  </div>
                                ) : trackingType === "quantity" ? (
                                  <Input type="number" min={0} max={qty} className="w-20 text-right h-7" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                                ) : (
                                  <Input type="number" min={0} max={100} step={5} className="w-16 text-right h-7" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                                )
                              ) : (
                                <div className="flex items-center gap-2 justify-end">
                                  <Progress value={completionPct} className="w-12 h-1.5" />
                                  <span>{pct(completionPct)}</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">{fmt(earned)}</TableCell>
                            <TableCell className={cn("text-right text-xs", billedAmt < earned && "text-amber-600")}>
                              {fmt(billedAmt)}
                            </TableCell>
                            <TableCell onClick={e => e.stopPropagation()}>
                              {isFieldEditing ? (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setFieldEditingLine(null)}>
                                    <X className="h-3 w-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={saving} onClick={() => handleSaveFieldEdit(wol.id)}>
                                    <Check className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : isEditing ? (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditingLine(null)}>✕</Button>
                                  <Button size="sm" className="h-6 px-2 text-xs" disabled={saving} onClick={() => handleSaveCompletion(wol, trackingType)}>✓</Button>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  {canEdit && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0"
                                            onClick={() => {
                                              setFieldEditingLine(wol.id);
                                              setFieldEditValues({
                                                description: wol.description ?? "",
                                                quantity: String(Number(wol.quantity)),
                                                contract_rate: String(Number(wol.contract_rate)),
                                                unit: wol.unit ?? "",
                                              });
                                            }}
                                          >
                                            <Pencil className="h-3 w-3" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Edit line item</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => {
                                      setEditingLine(wol.id);
                                      if (trackingType === "lump_sum") setEditValue(completionPct >= 100 ? "complete" : "not_started");
                                      else if (trackingType === "quantity") setEditValue(String(Number(wol.completed_qty ?? 0)));
                                      else setEditValue(String(completionPct));
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  {canEdit && wo.status !== "cancelled" && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                                      disabled={deletingLine === wol.id}
                                      onClick={() => handleDeleteLine(wol)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  );
                })}
                {/* Footer totals */}
                <TableRow className="bg-muted/30 font-medium">
                  <TableCell />
                  <TableCell colSpan={isSovOnly ? 5 : 4} className="text-right text-sm">
                    {consolidatedLines.length} items · Total
                  </TableCell>
                  <TableCell className="text-right text-sm font-bold">
                    {fmt(consolidatedLines.reduce((s, g) => s + g.lineTotal, 0))}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {pct(consolidatedLines.reduce((s, g) => s + g.completionPct, 0) / (consolidatedLines.length || 1))}
                  </TableCell>
                  <TableCell className="text-right text-sm">{fmt(consolidatedLines.reduce((s, g) => s + g.earned, 0))}</TableCell>
                  <TableCell className="text-right text-sm">{fmt(consolidatedLines.reduce((s, g) => s + g.billed, 0))}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="adjustments">
          <WOAdjustmentsTab
            workOrderId={wo.id}
            projectId={projectId}
            onMutate={onMutate}
          />
        </TabsContent>

        <TabsContent value="retainage">
          <WORetainageTab
            workOrder={wo}
            billedToDate={billed}
            onMutate={onMutate}
          />
        </TabsContent>

        {hasAnyUnits && !isBlanket && (
          <TabsContent value="billing">
            <WOBillingGrid
              wo={wo}
              woLines={linesForWo}
              sovLines={sovLines ?? []}
              projectId={projectId}
              project={project}
              onBillGenerated={onMutate}
            />
          </TabsContent>
        )}

        {hasAnyUnits && isBlanket && (
          <TabsContent value="completion">
            <WOCompletionGrid
              wo={wo}
              woLines={linesForWo}
              sovLines={sovLines ?? []}
              onMutate={onMutate}
            />
          </TabsContent>
        )}

        {hasAnyUnits && isBlanket && (
          <TabsContent value="draws">
            <WODrawHistory wo={wo} onMutate={onMutate} />
          </TabsContent>
        )}

        {hasAnyUnits && isBlanket && (
          <TabsContent value="new-draw">
            <WODrawGrid
              wo={wo}
              woLines={linesForWo}
              sovLines={sovLines ?? []}
              projectId={projectId}
              project={project}
              onDrawGenerated={onMutate}
            />
          </TabsContent>
        )}

        {(billsCount ?? 0) > 0 && (
          <TabsContent value="bills">
            <WOBillsList
              wo={wo}
              projectId={projectId}
              onMutate={onMutate}
              onOpenCreateBill={() => setBillDialogOpen(true)}
            />
          </TabsContent>
        )}

        {canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && billableCount > 0 && (
          <TabsContent value="create-bill">
            <div className="py-6 text-center space-y-3">
              <Receipt className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {billableCount} line{billableCount !== 1 ? "s" : ""} available to bill on {wo.number}
              </p>
              <Button onClick={() => setBillDialogOpen(true)}>
                <Receipt className="h-4 w-4 mr-2" /> Create Bill →
              </Button>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <WOCreateBillDialog
        open={billDialogOpen}
        onOpenChange={setBillDialogOpen}
        wo={wo}
        woLines={linesForWo}
        projectId={projectId}
        onCreated={onMutate}
      />
    </div>
  );
};
